// Mobile Authentication Service for MSG91 OTP Integration
// This service handles mobile-specific authentication flows

class MobileAuthService {
    constructor(apiBaseUrl) {
        this.apiBaseUrl = apiBaseUrl || process.env.REACT_APP_API_URL || 'https://myhomeentry.com/api';
        this.tokenKey = 'mobile_auth_tokens';
        this.userKey = 'mobile_user_data';
        
        // Retry tracking to prevent infinite loops
        this.refreshAttempts = 0;
        this.maxRefreshAttempts = 3;
        this.lastRefreshAttempt = 0;
        this.refreshCooldownMs = 30000; // 30 seconds cooldown between refresh attempts
    }

    /**
     * Send OTP to phone number
     */
    async sendOTP(phoneNumber) {
        try {
            const response = await fetch(`${this.apiBaseUrl}/msg91/send-otp`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ phoneNumber })
            });

            const result = await response.json();
            
            if (response.ok) {
                return { success: true, data: result };
            } else {
                return { 
                    success: false, 
                    error: result.error || 'Failed to send OTP',
                    message: result.message 
                };
            }
        } catch (error) {
            return { 
                success: false, 
                error: 'Network error', 
                message: error.message 
            };
        }
    }

    /**
     * Verify OTP and authenticate user
     */
    async verifyOTP(phoneNumber, otp) {
        try {
            const response = await fetch(`${this.apiBaseUrl}/msg91/verify-otp`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ phoneNumber, otp })
            });

            const result = await response.json();
            
            if (response.ok) {
                // Store tokens and user data if available
                if (result.tokens) {
                    this.storeTokens(result.tokens);
                }
                
                if (result.user) {
                    this.storeUserData(result.user);
                }

                return { success: true, data: result };
            } else {
                return { 
                    success: false, 
                    error: result.error || 'OTP verification failed',
                    message: result.message,
                    attemptsRemaining: result.attemptsRemaining
                };
            }
        } catch (error) {
            return { 
                success: false, 
                error: 'Network error', 
                message: error.message 
            };
        }
    }

    /**
     * Register new user after OTP verification
     */
    async registerUser(phoneNumber, firstName, lastName, email = null, password = null) {
        try {
            const response = await fetch(`${this.apiBaseUrl}/msg91/register`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ 
                    phoneNumber, 
                    firstName, 
                    lastName, 
                    email, 
                    password 
                })
            });

            const result = await response.json();
            
            if (response.ok) {
                return { success: true, data: result };
            } else {
                return { 
                    success: false, 
                    error: result.error || 'Registration failed',
                    message: result.message 
                };
            }
        } catch (error) {
            return { 
                success: false, 
                error: 'Network error', 
                message: error.message 
            };
        }
    }

    /**
     * Complete password challenge for new users
     */
    async completePasswordChallenge(phoneNumber, newPassword, session) {
        try {
            const response = await fetch(`${this.apiBaseUrl}/msg91/complete-password`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ phoneNumber, newPassword, session })
            });

            const result = await response.json();
            
            if (response.ok) {
                // Store tokens and user data
                if (result.tokens) {
                    this.storeTokens(result.tokens);
                }
                
                if (result.user) {
                    this.storeUserData(result.user);
                }

                return { success: true, data: result };
            } else {
                return { 
                    success: false, 
                    error: result.error || 'Password setup failed',
                    message: result.message 
                };
            }
        } catch (error) {
            return { 
                success: false, 
                error: 'Network error', 
                message: error.message 
            };
        }
    }

    /**
     * Refresh authentication tokens
     */
    async refreshTokens() {
        try {
            const now = Date.now();
            
            // Check cooldown period to prevent rapid retry attempts
            if (now - this.lastRefreshAttempt < this.refreshCooldownMs) {
                console.warn('[MobileAuth] Refresh token cooldown active, skipping attempt');
                return { success: false, error: 'Refresh cooldown active' };
            }
            
            // Check max attempts
            if (this.refreshAttempts >= this.maxRefreshAttempts) {
                console.warn('[MobileAuth] Max refresh attempts exceeded, clearing tokens');
                this.clearStoredData();
                this.resetRefreshAttempts();
                return { success: false, error: 'Max refresh attempts exceeded' };
            }
            
            const tokens = this.getStoredTokens();
            if (!tokens || !tokens.refreshToken) {
                this.resetRefreshAttempts();
                return { success: false, error: 'No refresh token available' };
            }

            // Increment attempt counter and update timestamp
            this.refreshAttempts++;
            this.lastRefreshAttempt = now;

            console.log(`[MobileAuth] Attempting token refresh (attempt ${this.refreshAttempts}/${this.maxRefreshAttempts})`);

            const response = await fetch(`${this.apiBaseUrl}/msg91/refresh-tokens`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ 
                    refreshToken: tokens.refreshToken,
                    phoneNumber: this.getStoredUserData()?.username 
                })
            });

            const result = await response.json();
            
            if (response.ok && result.tokens) {
                // Success - reset attempt counter
                this.resetRefreshAttempts();
                this.storeTokens(result.tokens);
                console.log('[MobileAuth] Token refresh successful');
                return { success: true, data: result };
            } else {
                console.warn('[MobileAuth] Token refresh failed:', result.error || result.message);
                
                // If this was the last attempt, clear tokens
                if (this.refreshAttempts >= this.maxRefreshAttempts) {
                    this.clearStoredData();
                    this.resetRefreshAttempts();
                }
                
                return { 
                    success: false, 
                    error: result.error || 'Token refresh failed',
                    message: result.message 
                };
            }
        } catch (error) {
            console.error('[MobileAuth] Token refresh network error:', error);
            
            // If this was the last attempt, clear tokens  
            if (this.refreshAttempts >= this.maxRefreshAttempts) {
                this.clearStoredData();
                this.resetRefreshAttempts();
            }
            
            return { 
                success: false, 
                error: 'Network error', 
                message: error.message 
            };
        }
    }

    /**
     * Reset refresh attempt tracking
     */
    resetRefreshAttempts() {
        this.refreshAttempts = 0;
        this.lastRefreshAttempt = 0;
    }

    /**
     * Resend OTP
     */
    async resendOTP(phoneNumber) {
        try {
            const response = await fetch(`${this.apiBaseUrl}/msg91/resend-otp`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ phoneNumber })
            });

            const result = await response.json();
            
            if (response.ok) {
                return { success: true, data: result };
            } else {
                return { 
                    success: false, 
                    error: result.error || 'Failed to resend OTP',
                    message: result.message 
                };
            }
        } catch (error) {
            return { 
                success: false, 
                error: 'Network error', 
                message: error.message 
            };
        }
    }

    /**
     * Store authentication tokens
     */
    storeTokens(tokens) {
        try {
            const tokenData = {
                ...tokens,
                timestamp: Date.now(),
                expiresAt: Date.now() + (tokens.expiresIn * 1000)
            };
            
            if (typeof window !== 'undefined') {
                localStorage.setItem(this.tokenKey, JSON.stringify(tokenData));
            } else if (typeof require !== 'undefined') {
                // For React Native or Node.js environments
                const AsyncStorage = require('@react-native-async-storage/async-storage').default;
                AsyncStorage.setItem(this.tokenKey, JSON.stringify(tokenData));
            }
        } catch (error) {
            console.error('Failed to store tokens:', error);
        }
    }

    /**
     * Store user data
     */
    storeUserData(userData) {
        try {
            if (typeof window !== 'undefined') {
                localStorage.setItem(this.userKey, JSON.stringify(userData));
            } else if (typeof require !== 'undefined') {
                const AsyncStorage = require('@react-native-async-storage/async-storage').default;
                AsyncStorage.setItem(this.userKey, JSON.stringify(userData));
            }
        } catch (error) {
            console.error('Failed to store user data:', error);
        }
    }

    /**
     * Get stored tokens
     */
    getStoredTokens() {
        try {
            if (typeof window !== 'undefined') {
                const stored = localStorage.getItem(this.tokenKey);
                return stored ? JSON.parse(stored) : null;
            } else if (typeof require !== 'undefined') {
                const AsyncStorage = require('@react-native-async-storage/async-storage').default;
                return AsyncStorage.getItem(this.tokenKey).then(stored => 
                    stored ? JSON.parse(stored) : null
                );
            }
        } catch (error) {
            console.error('Failed to get stored tokens:', error);
            return null;
        }
        return null;
    }

    /**
     * Get stored user data
     */
    getStoredUserData() {
        try {
            if (typeof window !== 'undefined') {
                const stored = localStorage.getItem(this.userKey);
                return stored ? JSON.parse(stored) : null;
            } else if (typeof require !== 'undefined') {
                const AsyncStorage = require('@react-native-async-storage/async-storage').default;
                return AsyncStorage.getItem(this.userKey).then(stored => 
                    stored ? JSON.parse(stored) : null
                );
            }
        } catch (error) {
            console.error('Failed to get stored user data:', error);
            return null;
        }
        return null;
    }

    /**
     * Get current access token for API calls
     */
    async getAccessToken() {
        const tokens = this.getStoredTokens();
        
        if (!tokens) {
            return null;
        }

        // Check if token is expired
        if (Date.now() > tokens.expiresAt) {
            // Try to refresh token with proper error handling
            try {
                console.log('[MobileAuth] Token expired, attempting refresh');
                const refreshResult = await this.refreshTokens();
                
                if (refreshResult.success) {
                    const newTokens = this.getStoredTokens();
                    return newTokens ? newTokens.accessToken : null;
                } else {
                    console.warn('[MobileAuth] Token refresh failed:', refreshResult.error);
                    // Don't immediately clear - let the refresh retry logic handle it
                    return null;
                }
            } catch (error) {
                console.error('[MobileAuth] Error during token refresh:', error);
                return null;
            }
        }

        return tokens.accessToken;
    }

    /**
     * Check if user is authenticated
     */
    async isAuthenticated() {
        const token = await this.getAccessToken();
        const userData = this.getStoredUserData();
        return !!(token && userData);
    }

    /**
     * Clear stored authentication data
     */
    clearStoredData() {
        try {
            if (typeof window !== 'undefined') {
                localStorage.removeItem(this.tokenKey);
                localStorage.removeItem(this.userKey);
            } else if (typeof require !== 'undefined') {
                const AsyncStorage = require('@react-native-async-storage/async-storage').default;
                AsyncStorage.removeItem(this.tokenKey);
                AsyncStorage.removeItem(this.userKey);
            }
            
            // Reset refresh attempt tracking when clearing data
            this.resetRefreshAttempts();
            console.log('[MobileAuth] Stored data and refresh attempts cleared');
        } catch (error) {
            console.error('Failed to clear stored data:', error);
        }
    }

    /**
     * Logout user
     */
    logout() {
        this.clearStoredData();
    }

    /**
     * Make authenticated API request
     */
    async makeAuthenticatedRequest(endpoint, options = {}) {
        const token = await this.getAccessToken();
        
        if (!token) {
            throw new Error('No valid authentication token');
        }

        const headers = {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            ...(options.headers || {})
        };

        const response = await fetch(`${this.apiBaseUrl}${endpoint}`, {
            ...options,
            headers
        });

        if (response.status === 401) {
            // Token might be expired, try to refresh
            const refreshResult = await this.refreshTokens();
            if (refreshResult.success) {
                // Retry the original request with new token
                const newToken = await this.getAccessToken();
                if (newToken) {
                    headers.Authorization = `Bearer ${newToken}`;
                    return fetch(`${this.apiBaseUrl}${endpoint}`, {
                        ...options,
                        headers
                    });
                }
            }
            // If refresh failed, clear data and throw error
            this.clearStoredData();
            throw new Error('Authentication failed');
        }

        return response;
    }
}

// Export for different module systems
if (typeof module !== 'undefined' && module.exports) {
    module.exports = MobileAuthService;
} else if (typeof window !== 'undefined') {
    window.MobileAuthService = MobileAuthService;
}

export default MobileAuthService;